import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import {
  Zap,
  ArrowLeft,
  Send,
  User,
  Bot,
  FileText,
  CheckCircle,
  Download,
  Clock,
  Timer,
  AlertTriangle,
  Play,
  Home,
  Mic,
  MicOff,
  Square,
  Loader2,
  Video,
  VideoOff,
  StopCircle,
  Volume2,
  VolumeX
} from "lucide-react";

const AIInterviewer = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [session, setSession] = useState(null);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [summarizing, setSummarizing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [starting, setStarting] = useState(false);
  const [remainingTime, setRemainingTime] = useState(null);
  const messagesEndRef = useRef(null);
  const timerRef = useRef(null);
  
  // Voice mode state
  const [isVoiceMode, setIsVoiceMode] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);

  // TTS (Text-to-Speech) state
  const [ttsEnabled, setTtsEnabled] = useState(true);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const audioRef = useRef(null);

  // Video recording state
  const [isVideoMode, setIsVideoMode] = useState(false);
  const [isVideoRecording, setIsVideoRecording] = useState(false);
  const [videoBlob, setVideoBlob] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const videoRecorderRef = useRef(null);
  const videoStreamRef = useRef(null);
  const videoChunksRef = useRef([]);
  const videoPreviewRef = useRef(null);

  const isCandidate = session?.candidate_id === user?.user_id;
  const isRecruiter = session?.recruiter_id === user?.user_id;

  // TTS function to speak AI responses
  const speakText = async (text) => {
    if (!ttsEnabled || !text) return;
    
    setIsSpeaking(true);
    try {
      const response = await fetch(`${API}/ai/interview/tts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ text }),
      });

      if (response.ok) {
        const data = await response.json();
        const audioSrc = `data:audio/mp3;base64,${data.audio_base64}`;
        
        if (audioRef.current) {
          audioRef.current.src = audioSrc;
          audioRef.current.play();
        }
      }
    } catch (error) {
      console.error("TTS failed:", error);
    } finally {
      setIsSpeaking(false);
    }
  };

  useEffect(() => {
    fetchSession();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [sessionId]);

  useEffect(() => {
    scrollToBottom();
  }, [session?.messages]);

  // Timer countdown
  useEffect(() => {
    if (session?.status === "active" && session?.remaining_seconds) {
      setRemainingTime(session.remaining_seconds);
      
      timerRef.current = setInterval(() => {
        setRemainingTime(prev => {
          if (prev <= 0) {
            clearInterval(timerRef.current);
            fetchSession(); // Refresh to get timed_out status
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [session?.status, session?.remaining_seconds]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchSession = async () => {
    try {
      const response = await fetch(`${API}/ai/interview/${sessionId}`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setSession(data);
        if (data.remaining_seconds) {
          setRemainingTime(data.remaining_seconds);
        }
      } else {
        toast.error("Interview session not found");
        navigate(-1);
      }
    } catch (error) {
      console.error("Failed to fetch session:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartInterview = async () => {
    setStarting(true);
    try {
      const response = await fetch(`${API}/ai/interview/${sessionId}/begin`, {
        method: "POST",
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setSession(data);
        setRemainingTime(data.time_limit_minutes * 60);
        toast.success("Interview started! Good luck!");
        
        // Speak the first AI message
        if (ttsEnabled && data.messages?.length > 0) {
          const firstAiMessage = data.messages.find(m => m.role === "assistant");
          if (firstAiMessage) {
            speakText(firstAiMessage.content);
          }
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to start interview");
      }
    } catch (error) {
      toast.error("Failed to start interview");
    } finally {
      setStarting(false);
    }
  };

  const handleSendMessage = async () => {
    if (!message.trim() || sending) return;

    setSending(true);
    try {
      const response = await fetch(`${API}/ai/interview/${sessionId}/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message: message.trim() }),
      });

      if (response.ok) {
        const data = await response.json();
        setSession(prev => ({
          ...prev,
          messages: [
            ...prev.messages,
            { role: "user", content: message.trim() },
            { role: "assistant", content: data.message }
          ],
          status: data.status,
          questions_asked: data.questions_asked
        }));
        setMessage("");
        
        // Speak the AI response
        if (ttsEnabled && data.message) {
          speakText(data.message);
        }
        
        if (data.remaining_seconds !== undefined) {
          setRemainingTime(data.remaining_seconds);
        }
        
        if (data.status === "completed") {
          toast.success("Interview completed! Great job!");
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to send message");
      }
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const handleSummarize = async () => {
    setSummarizing(true);
    try {
      const response = await fetch(`${API}/ai/interview/${sessionId}/summarize`, {
        method: "POST",
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setSession(prev => ({ ...prev, summary: data.summary }));
        toast.success("Interview summarized!");
      } else {
        toast.error("Failed to summarize");
      }
    } catch (error) {
      toast.error("Failed to summarize");
    } finally {
      setSummarizing(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await fetch(`${API}/ai/interview/${sessionId}/export`, {
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `interview_${sessionId}_${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success("Interview exported successfully!");
      } else {
        toast.error("Failed to export interview");
      }
    } catch (error) {
      toast.error("Failed to export interview");
    } finally {
      setExporting(false);
    }
  };

  // Voice recording functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      mediaRecorderRef.current = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        await transcribeAudio(audioBlob);
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start recording timer
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
    } catch (error) {
      console.error("Failed to start recording:", error);
      toast.error("Could not access microphone. Please allow microphone permissions.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    }
  };

  const transcribeAudio = async (audioBlob) => {
    setIsTranscribing(true);
    
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      
      const response = await fetch(`${API}/ai/transcribe`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.text) {
          setMessage(data.text);
          toast.success("Audio transcribed successfully!");
        } else {
          toast.error("No speech detected");
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to transcribe audio");
      }
    } catch (error) {
      console.error("Transcription failed:", error);
      toast.error("Failed to transcribe audio");
    } finally {
      setIsTranscribing(false);
      setRecordingTime(0);
    }
  };

  const formatRecordingTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Video recording functions
  const startVideoRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { width: 1280, height: 720 },
        audio: true 
      });
      
      videoStreamRef.current = stream;
      
      // Show preview
      if (videoPreviewRef.current) {
        videoPreviewRef.current.srcObject = stream;
        videoPreviewRef.current.play();
      }
      
      const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus') 
        ? 'video/webm;codecs=vp9,opus'
        : 'video/webm';
      
      videoRecorderRef.current = new MediaRecorder(stream, { mimeType });
      videoChunksRef.current = [];
      
      videoRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          videoChunksRef.current.push(event.data);
        }
      };
      
      videoRecorderRef.current.onstop = async () => {
        const blob = new Blob(videoChunksRef.current, { type: mimeType });
        setVideoBlob(blob);
        const url = URL.createObjectURL(blob);
        setVideoUrl(url);
        
        // Stop the preview stream
        if (videoStreamRef.current) {
          videoStreamRef.current.getTracks().forEach(track => track.stop());
        }
      };
      
      videoRecorderRef.current.start(1000); // Record in 1-second chunks
      setIsVideoRecording(true);
      toast.success("Video recording started!");
      
    } catch (error) {
      console.error("Failed to start video recording:", error);
      toast.error("Could not access camera. Please allow camera permissions.");
    }
  };

  const stopVideoRecording = () => {
    if (videoRecorderRef.current && isVideoRecording) {
      videoRecorderRef.current.stop();
      setIsVideoRecording(false);
      toast.success("Video recording saved!");
    }
  };

  const uploadVideoRecording = async () => {
    if (!videoBlob || !sessionId) return;
    
    setUploadingVideo(true);
    try {
      const formData = new FormData();
      formData.append('video', videoBlob, `interview_${sessionId}.webm`);
      formData.append('interview_id', sessionId);
      formData.append('recording_type', 'full');
      formData.append('duration_seconds', '0');
      
      const response = await fetch(`${API}/video-recordings/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success("Video uploaded successfully!");
        setSession(prev => ({ ...prev, video_recording: data.recording_id }));
        // Clean up
        setVideoBlob(null);
        if (videoUrl) URL.revokeObjectURL(videoUrl);
        setVideoUrl(null);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to upload video");
      }
    } catch (error) {
      console.error("Video upload failed:", error);
      toast.error("Failed to upload video");
    } finally {
      setUploadingVideo(false);
    }
  };

  const discardVideoRecording = () => {
    if (videoUrl) URL.revokeObjectURL(videoUrl);
    setVideoBlob(null);
    setVideoUrl(null);
    toast.info("Video recording discarded");
  };

  const toggleVideoMode = () => {
    if (isVideoMode && isVideoRecording) {
      stopVideoRecording();
    }
    if (isVideoMode && videoStreamRef.current) {
      videoStreamRef.current.getTracks().forEach(track => track.stop());
    }
    setIsVideoMode(!isVideoMode);
    setVideoBlob(null);
    if (videoUrl) URL.revokeObjectURL(videoUrl);
    setVideoUrl(null);
  };

  const formatTime = (seconds) => {
    if (seconds === null || seconds === undefined) return "--:--";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const dashboardLink = user?.role === "recruiter" ? "/dashboard/recruiter" : "/dashboard/jobseeker";

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-crimson border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!session) return null;

  return (
    <div className="min-h-screen bg-obsidian">
      <Sidebar />
      
      <main className="lg:ml-64 min-h-screen flex flex-col pt-16 lg:pt-0">
        {/* Header Bar */}
        <div className="bg-obsidian/80 backdrop-blur-md border-b border-[#222] px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="font-heading text-xl font-bold">
              AI <span className="text-crimson">INTERVIEWER</span>
            </h1>
          </div>
          <div className="flex items-center gap-4">
            {/* TTS Toggle */}
            {isCandidate && session.status === "active" && (
              <button
                onClick={() => setTtsEnabled(!ttsEnabled)}
                className={`p-2 transition-colors ${
                  ttsEnabled ? "bg-green-500/20 text-green-400" : "bg-gray-500/20 text-gray-400"
                }`}
                title={ttsEnabled ? "Voice enabled" : "Voice disabled"}
                data-testid="tts-toggle"
              >
                {ttsEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
              </button>
            )}
            
            {/* Timer */}
            {session.status === "active" && remainingTime !== null && (
              <div className={`flex items-center gap-2 px-4 py-2 border ${
                remainingTime < 60 ? "bg-red-500/20 border-red-500/50 text-red-400" :
                remainingTime < 300 ? "bg-yellow-500/20 border-yellow-500/50 text-yellow-400" :
                "bg-green-500/20 border-green-500/50 text-green-400"
              }`}>
                <Timer className="w-4 h-4" />
                <span className="font-mono font-bold">{formatTime(remainingTime)}</span>
              </div>
            )}
            
            {/* Progress */}
            {session.status === "active" && (
              <div className="text-sm text-gray-400">
                Q{session.questions_asked || 1}/{session.num_questions || 6}
              </div>
            )}
            
            {/* Status Badge */}
            <span className={`px-3 py-1 text-sm uppercase tracking-wider ${
              session.status === "completed" ? "bg-green-500/20 text-green-400" :
              session.status === "timed_out" ? "bg-red-500/20 text-red-400" :
              session.status === "ready" ? "bg-blue-500/20 text-blue-400" :
              "bg-crimson/20 text-crimson"
            }`}>
              {session.status === "timed_out" ? "Time Up" : session.status}
            </span>
          </div>
        </div>

      {/* Hidden Audio Element for TTS */}
      <audio ref={audioRef} onEnded={() => setIsSpeaking(false)} />

      {/* Main Content */}
      <div className="flex-1 pb-24 px-6 overflow-hidden">
        <div className="max-w-4xl mx-auto h-full flex flex-col">
          {/* Job Info */}
          <div className="bg-obsidian-light border border-[#222] p-4 mb-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Interview for:</p>
              <p className="font-heading text-lg font-bold">{session.job_title}</p>
            </div>
            {session.time_limit_minutes && (
              <div className="text-right text-sm">
                <p className="text-gray-500">Time Limit</p>
                <p className="text-white font-bold">{session.time_limit_minutes} minutes</p>
              </div>
            )}
          </div>

          {/* Ready State - Show start button */}
          {session.status === "ready" && isCandidate && (
            <motion.div
              className="bg-obsidian-light border border-crimson/30 p-8 mb-4 text-center"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <div className="w-16 h-16 bg-crimson/20 flex items-center justify-center mx-auto mb-4">
                <Play className="w-8 h-8 text-crimson" />
              </div>
              <h2 className="font-heading text-2xl font-bold mb-2">Ready to Begin?</h2>
              <p className="text-gray-400 mb-6 max-w-md mx-auto">
                This interview has {session.num_questions} questions and a time limit of {session.time_limit_minutes} minutes.
                Once you start, the timer will begin. Take a deep breath and do your best!
              </p>
              <Button
                onClick={handleStartInterview}
                disabled={starting}
                className="bg-crimson hover:bg-crimson-hover text-white font-bold uppercase tracking-wider px-8 py-6 text-lg"
                data-testid="start-interview-btn"
              >
                {starting ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Play className="w-5 h-5 mr-2" /> Start Interview
                  </>
                )}
              </Button>
            </motion.div>
          )}

          {/* Timed Out State */}
          {session.status === "timed_out" && (
            <div className="bg-red-500/10 border border-red-500/30 p-6 mb-4 flex items-center gap-4">
              <AlertTriangle className="w-8 h-8 text-red-400 flex-shrink-0" />
              <div>
                <h3 className="font-heading text-lg font-bold text-red-400">Time Up!</h3>
                <p className="text-gray-400">
                  The interview time limit has been reached. Your responses have been saved.
                </p>
              </div>
            </div>
          )}

          {/* Messages */}
          {(session.status === "active" || session.status === "completed" || session.status === "timed_out") && (
            <div className="flex-1 overflow-y-auto space-y-4 terminal p-6 mb-4">
              <div className="terminal-header -m-6 mb-6">
                <div className="terminal-dot bg-crimson" />
                <div className="terminal-dot bg-yellow-500" />
                <div className="terminal-dot bg-green-500" />
                <span className="ml-2 text-xs text-gray-500 font-mono">AI_INTERVIEW_SESSION</span>
              </div>

              {session.messages.map((msg, index) => (
                <motion.div
                  key={index}
                  className={`flex gap-3 ${msg.role === "user" ? "justify-end" : ""}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  {msg.role === "assistant" && (
                    <div className="w-8 h-8 bg-crimson/20 flex items-center justify-center flex-shrink-0">
                      <Bot className="w-4 h-4 text-crimson" />
                    </div>
                  )}
                  <div className={`max-w-[80%] p-4 ${
                    msg.role === "user" 
                      ? "bg-crimson/10 border border-crimson/30" 
                      : "bg-[#0A0A0A] border border-[#222]"
                  }`}>
                    <pre className="whitespace-pre-wrap font-mono text-sm text-gray-200">
                      {msg.content}
                    </pre>
                  </div>
                  {msg.role === "user" && (
                    <div className="w-8 h-8 bg-white/10 flex items-center justify-center flex-shrink-0">
                      <User className="w-4 h-4 text-white" />
                    </div>
                  )}
                </motion.div>
              ))}

              {sending && (
                <div className="flex gap-3">
                  <div className="w-8 h-8 bg-crimson/20 flex items-center justify-center flex-shrink-0">
                    <Bot className="w-4 h-4 text-crimson" />
                  </div>
                  <div className="bg-[#0A0A0A] border border-[#222] p-4">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 bg-crimson rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                      <div className="w-2 h-2 bg-crimson rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                      <div className="w-2 h-2 bg-crimson rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          )}

          {/* Interview Complete */}
          {session.status === "completed" && isCandidate && !session.summary && (
            <div className="bg-green-500/10 border border-green-500/30 p-4 mb-4 flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-400" />
              <span className="text-green-400">Interview completed! Thank you for your responses.</span>
            </div>
          )}

          {/* Summary (for recruiters) */}
          {session.summary && isRecruiter && (
            <motion.div 
              className="bg-obsidian-light border border-crimson/30 p-6 mb-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading text-lg font-bold flex items-center gap-2">
                  <FileText className="w-5 h-5 text-crimson" />
                  INTERVIEW SUMMARY
                </h3>
                <Button
                  onClick={handleExport}
                  disabled={exporting}
                  variant="outline"
                  className="border-[#333] hover:border-crimson text-white"
                  data-testid="export-btn"
                >
                  {exporting ? (
                    <div className="w-4 h-4 border-2 border-crimson border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" /> Export
                    </>
                  )}
                </Button>
              </div>
              <pre className="whitespace-pre-wrap font-body text-sm text-gray-300">
                {session.summary}
              </pre>
            </motion.div>
          )}

          {/* Input (for candidates) */}
          {isCandidate && session.status === "active" && (
            <div className="space-y-3">
              {/* Mode Toggles */}
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setIsVoiceMode(!isVoiceMode)}
                    className={`flex items-center gap-2 px-3 py-1.5 text-sm transition-colors ${
                      isVoiceMode 
                        ? "bg-crimson/20 text-crimson border border-crimson/50" 
                        : "bg-[#1a1a1a] text-gray-400 border border-[#333] hover:text-white"
                    }`}
                    data-testid="voice-mode-toggle"
                  >
                    {isVoiceMode ? <Mic className="w-4 h-4" /> : <MicOff className="w-4 h-4" />}
                    Voice {isVoiceMode ? "ON" : "OFF"}
                  </button>
                  
                  <button
                    onClick={toggleVideoMode}
                    className={`flex items-center gap-2 px-3 py-1.5 text-sm transition-colors ${
                      isVideoMode 
                        ? "bg-blue-600/20 text-blue-400 border border-blue-500/50" 
                        : "bg-[#1a1a1a] text-gray-400 border border-[#333] hover:text-white"
                    }`}
                    data-testid="video-mode-toggle"
                  >
                    {isVideoMode ? <Video className="w-4 h-4" /> : <VideoOff className="w-4 h-4" />}
                    Video {isVideoMode ? "ON" : "OFF"}
                  </button>
                </div>
                
                {isVoiceMode && !isRecording && !isTranscribing && (
                  <span className="text-xs text-gray-500">Click microphone to start recording</span>
                )}
              </div>

              {/* Video Recording UI */}
              <AnimatePresence>
                {isVideoMode && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="bg-obsidian-light border border-blue-500/30 p-4"
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <Video className="w-4 h-4 text-blue-400" />
                      <span className="text-sm font-medium text-blue-400">Video Recording</span>
                    </div>
                    
                    {/* Video Preview / Playback */}
                    <div className="relative aspect-video bg-black mb-3 overflow-hidden">
                      {videoUrl ? (
                        <video 
                          src={videoUrl} 
                          controls 
                          className="w-full h-full object-contain"
                          data-testid="video-playback"
                        />
                      ) : (
                        <video 
                          ref={videoPreviewRef} 
                          muted 
                          className={`w-full h-full object-cover ${isVideoRecording ? "" : "opacity-50"}`}
                          data-testid="video-preview"
                        />
                      )}
                      {isVideoRecording && (
                        <div className="absolute top-3 left-3 flex items-center gap-2 bg-black/70 px-2 py-1 rounded">
                          <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                          <span className="text-xs text-white">REC</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Video Controls */}
                    <div className="flex items-center justify-center gap-3">
                      {videoBlob ? (
                        <>
                          <Button
                            onClick={uploadVideoRecording}
                            disabled={uploadingVideo}
                            className="bg-green-600 hover:bg-green-700 text-white"
                            data-testid="upload-video-btn"
                          >
                            {uploadingVideo ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <CheckCircle className="w-4 h-4 mr-2" />
                            )}
                            Save Recording
                          </Button>
                          <Button
                            onClick={discardVideoRecording}
                            variant="outline"
                            className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                            data-testid="discard-video-btn"
                          >
                            Discard
                          </Button>
                        </>
                      ) : isVideoRecording ? (
                        <Button
                          onClick={stopVideoRecording}
                          className="bg-red-600 hover:bg-red-700 text-white"
                          data-testid="stop-video-btn"
                        >
                          <StopCircle className="w-4 h-4 mr-2" /> Stop Recording
                        </Button>
                      ) : (
                        <Button
                          onClick={startVideoRecording}
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                          data-testid="start-video-btn"
                        >
                          <Video className="w-4 h-4 mr-2" /> Start Recording
                        </Button>
                      )}
                    </div>
                    
                    {session?.video_recording && (
                      <div className="mt-3 pt-3 border-t border-[#333] flex items-center gap-2 text-green-400 text-sm">
                        <CheckCircle className="w-4 h-4" />
                        Video recording saved for this interview
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Voice Recording UI */}
              <AnimatePresence>
                {isVoiceMode && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="bg-obsidian-light border border-[#222] p-4"
                  >
                    {isTranscribing ? (
                      <div className="flex items-center justify-center gap-3 py-4">
                        <Loader2 className="w-6 h-6 text-crimson animate-spin" />
                        <span className="text-gray-400">Transcribing your response...</span>
                      </div>
                    ) : isRecording ? (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                          <span className="text-white font-mono">{formatRecordingTime(recordingTime)}</span>
                          <span className="text-gray-500 text-sm">Recording...</span>
                        </div>
                        <Button
                          onClick={stopRecording}
                          className="bg-red-600 hover:bg-red-700 text-white"
                          data-testid="stop-recording-btn"
                        >
                          <Square className="w-4 h-4 mr-2" /> Stop
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center">
                        <button
                          onClick={startRecording}
                          className="w-16 h-16 bg-crimson/20 border-2 border-crimson hover:bg-crimson/30 transition-colors flex items-center justify-center group"
                          data-testid="start-recording-btn"
                        >
                          <Mic className="w-8 h-8 text-crimson group-hover:scale-110 transition-transform" />
                        </button>
                      </div>
                    )}
                    
                    {message && !isRecording && !isTranscribing && (
                      <div className="mt-3 pt-3 border-t border-[#333]">
                        <p className="text-xs text-gray-500 mb-1">Transcribed text:</p>
                        <p className="text-sm text-gray-300 italic">{message}</p>
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Text Input */}
              <div className="flex gap-3">
                <Input
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleSendMessage()}
                  placeholder={isVoiceMode ? "Or type your response here..." : "Type your response..."}
                  className="bg-obsidian border-[#333] text-white placeholder:text-gray-600 h-12"
                  disabled={sending || isRecording || isTranscribing}
                  data-testid="interview-input"
                />
                <Button
                  onClick={handleSendMessage}
                  disabled={!message.trim() || sending || isRecording || isTranscribing}
                  className="bg-crimson hover:bg-crimson-hover text-white px-6"
                  data-testid="send-btn"
                >
                  {sending ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Summarize Button (for recruiters) */}
          {isRecruiter && (session.status === "completed" || session.status === "timed_out") && !session.summary && (
            <Button
              onClick={handleSummarize}
              disabled={summarizing}
              className="bg-crimson hover:bg-crimson-hover text-white font-bold uppercase tracking-wider w-full"
              data-testid="summarize-btn"
            >
              {summarizing ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <FileText className="w-4 h-4 mr-2" /> Generate Summary
                </>
              )}
            </Button>
          )}
        </div>
      </div>
      </main>
    </div>
  );
};

export default AIInterviewer;
